import { useEffect, useState } from "react";
import { DEFAULT_PAGE_SIZE, USER_TYPE } from "../../configs/constants";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import {
  Table as CSDTable,
  Box,
  Header,
} from "@cloudscape-design/components-themed";
import { Link } from "react-router-dom";
import type { RootState } from "../../app/store";
import { useAppSelector } from "../../app/hook";
import {
  useGetDivisionQuery,
  useGetGwCompaniesQuery,
} from "../../features/api";
import { Division } from "../../features/api-interfaces";
import {
  COMMON_LABELS,
  getDivisionItems,
  getDivisionLabel,
  DivisionDict,
} from "../default-constants";
import { LABELS } from "./constants";
import {
  getStatusLoc,
  getSettingValueLoc,
  getContractStatusLog,
  isLink,
  isLinkIpidValue,
} from "./util";
import { useCanIShowHook } from "../../app/useCanIShowHook";

const columnDefinitions = (isDocomoUser: boolean) => {
  return [
    {
      id: "gwCompanyCode",
      header: LABELS.COMPANY_NAME,
      cell: (e: any) => (
        // GW事業者情報登録・更新
        <Link to={"/gateway-companies/" + e.gwCompanyCode}>
          {e.gwCompanyName}
        </Link>
      ),
    },
    {
      id: "contractServiceName",
      header: LABELS.SERVICE_NAME,
      cell: (e: any) => e.contractServiceName,
    },
    {
      id: "gwName",
      header: LABELS.GW_NAME,
      cell: (e: any) =>
        // GW契約内容登録・更新
        isDocomoUser ? (
          <Link
            to={
              "/gateway-companies-contracts/" +
              e.gwCompanyCode +
              "/" +
              e.gatewayNo
            }
          >
            {e.gwName}
          </Link>
        ) : (
          e.gwName
        ),
    },
    {
      id: "contractStatus",
      header: LABELS.CONTRACT_STATUS,
      cell: (e: any) => getContractStatusLog(e.contractStoppedFlag),
    },
    {
      id: "connection",
      header: LABELS.CONNECTION_ENVIRONMENTAL,
      cell: (e: any) =>
        isLink(e.connectionEnvApprovalStatus, isDocomoUser) ? (
          // TODO URL
          <Link to="#">{getStatusLoc(e.connectionEnvApprovalStatus)}</Link>
        ) : (
          getStatusLoc(e.connectionEnvApprovalStatus)
        ),
    },
    {
      id: "implement",
      header: LABELS.IMPLEMENTATION_CONFIRMATION,
      cell: (e: any) =>
        isLink(e.impleConfirmationApprovalStatus, isDocomoUser) ? (
          // TODO URL
          <Link to="#">{getStatusLoc(e.impleConfirmationApprovalStatus)}</Link>
        ) : (
          getStatusLoc(e.impleConfirmationApprovalStatus)
        ),
    },
    {
      id: "ipid",
      header: LABELS.IPID_PRIOR_PAYOUT,
      cell: (e: any) =>
        isLinkIpidValue(e.priorPayoutIpidRuleSettingStatus) ? (
          // TODO URL
          <Link to="#">
            {getSettingValueLoc(e.priorPayoutIpidRuleSettingStatus)}
          </Link>
        ) : (
          getSettingValueLoc(e.priorPayoutIpidRuleSettingStatus)
        ),
    },
  ];
};

/**
 * GW一覧
 */
const Table = () => {
  // 事業者種別取得
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);

  const searchCondition = useAppSelector(
    (state: RootState) => state.gwSearchCondition
  );

  // 共通データを取得
  // 区分取得API呼び出し(親区分コード：3:契約サービス区分コード)
  const divisionData1 = useGetDivisionQuery("3");
  const contractServiceDivisions = divisionData1?.data as Division[];
  const contractServiceKbnItems: DivisionDict[] = getDivisionItems(
    contractServiceDivisions
  );

  // 一覧データの取得
  const { data, isFetching } = useGetGwCompaniesQuery(searchCondition);
  // 一覧データの加工
  const listData = data
    ? data.map((item: any) => ({
        ...item,
        contractServiceName: getDivisionLabel(
          contractServiceKbnItems,
          item.contractServiceCode
        ),
      }))
    : [];

  /**ページャ関連 */
  const options = columnDefinitions(isDocomoUser).map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  useEffect(() => {
    if (listData !== undefined) {
      setPagedItems(getPagedItems(listData, 1, preferences.pageSize));
      setAllItems(listData);
      setCurrentPageIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    setPagedItems(listData.slice(0, newPageSize));
    setCurrentPageIndex(1);
  };

  const visibleColumnList = isDocomoUser
    ? [
        "gwCompanyCode",
        "contractServiceName",
        "gwName",
        "contractStatus",
        "connection",
        "implement",
        "ipid",
      ]
    : [
        "gwCompanyCode",
        "contractServiceName",
        "gwName",
        "connection",
        "implement",
      ];

  return (
    <CSDTable
      columnDefinitions={columnDefinitions(isDocomoUser)}
      loading={isFetching}
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      items={pagedItems}
      resizableColumns
      stickyHeader
      header={
        <Header>
          {LABELS.TITLE}
          {getHeaderCounterText(allItems.length, pagedItems.length)}
        </Header>
      }
      visibleColumns={visibleColumnList}
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default Table;
